// cSpell:ignore Eigenaarsaandeel forfait ownerbase pand Samenvatting sleutel Tijdelijke verdeelsleutel Verdeling
/* eslint-disable no-nested-ternary */
// Per-pand preview of a building cost's split, rendered with the standard
// Rentio DataTable (same columns and treatment as the settlement Samenvatting)
// instead of a bespoke grid.

import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  Banner,
  DataTable,
  Select,
  Stack,
  TextStyle,
} from "@rentiohq/web-shared/dist/components";
import React from "react";
import styled from "styled-components";
import { costRowsVat, ICostVatRow, TCostBilling } from "../costVat.utils";
import { BuildingBreakdown, KeyCoverage } from "../engine";
import { keyDisplayName } from "../keyName.utils";
import { roundCents, vatOfAmount } from "../money.utils";
import { formatWeight } from "../numberInput.utils";
import { readingRowShare } from "../readingSplit.utils";
import { ApportionmentKey } from "../types";
import { MoneyWithVat } from "./Money";

// A verdeelsleutel is a stored list of weights per unit id, so it silently
// goes stale: add an apartment to the building and it is simply not in the
// sleutel, which means € 0 for that pand and a bigger share for the others.
// Nothing in the split itself reveals that, hence this banner.
export const KeyCoverageWarning: React.FC<{
  coverage: KeyCoverage;
  units: { id: string; label: string }[];
  keyName?: string;
}> = ({ coverage, units, keyName }) => {
  const named = keyName ? ` "${keyDisplayName(keyName)}"` : "";
  if (coverage.fellBackToEqual) {
    return (
      <Banner variation="warning" hasDismiss={false}>
        {getLocalizedText("settlement.distribution.key_covers_nothing", {
          named,
        })}
      </Banner>
    );
  }
  if (coverage.uncoveredUnitIds.length === 0) return null;
  const labels = coverage.uncoveredUnitIds
    .map(id => units.find(u => u.id === id)?.label ?? id)
    .join(", ");
  const one = coverage.uncoveredUnitIds.length === 1;
  return (
    <Banner variation="warning" hasDismiss={false}>
      {getLocalizedText(
        one
          ? "settlement.distribution.missing_units_one"
          : "settlement.distribution.missing_units_many",
        { named, labels },
      )}
    </Banner>
  );
};

const fmtNl = (iso: string) => {
  const [y, m, d] = iso.slice(0, 10).split("-");
  return `${d}/${m}/${y}`;
};

// The settlement summary's table look (Lewis, 2026-10-21): no frame and no
// gray bands, only a hairline between the rows; the "Verdeling" (3) and
// "Tijdelijke basis" (4) columns centered as there.
const AlignedTable = styled.div`
  table {
    width: 100%;
  }
  thead th {
    white-space: nowrap;
  }
  tbody td {
    border-bottom: 1px solid ${({ theme }) => theme.colors.neutral20};
    vertical-align: top;
  }
  tbody tr:last-child td {
    border-bottom: none;
  }

  /* Ratios and amounts never wrap; the two name columns take the slack. */
  td:nth-child(3),
  td:nth-child(4),
  td:nth-child(5) {
    white-space: nowrap;
  }
  th:nth-child(3),
  td:nth-child(3),
  th:nth-child(4),
  td:nth-child(4) {
    text-align: center;
  }
`;

// The total under the table, as the summary's: a line, then the label
// right-aligned next to its amount.
const TotalRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 24px;
  padding: 12px 16px;
  border-top: 1px solid ${({ theme }) => theme.colors.neutral20};
`;

interface DisplayRow {
  kind: "tenant" | "vacancy" | "owner";
  // A tenant whose lease prices the cost's kind as a forfait.
  isFixedFee?: boolean;
  // Who carries the row, for its VAT: the tenant's lease, or the unit whose
  // owner carries its vacancy and owner share.
  party: Omit<ICostVatRow, "amount">;
  key: string;
  name: string;
  fromIso?: string;
  toIso?: string;
  days: number; // for the "Tijdelijke basis" column (-1 renders as "—")
  amount: number;
}

// Amount + its VAT on a subdued sub-line — the shared table convention.
const amountCell = (excl: number, btw: number): React.ReactNode => (
  <MoneyWithVat amount={excl} vat={btw} variation="strong" />
);

export interface BuildingDistributionProps {
  keys: ApportionmentKey[];
  selectedKeyId?: string;
  onSelectKey: (id: string) => void;
  breakdown: BuildingBreakdown;
  vatRate: number; // VAT percentage for the btw line under each amount
  // How the tenants are billed, which decides the VAT on their rows (see
  // costVat.utils).
  billing: TCostBilling;
  isShared: boolean; // also show a per-unit "Eigenaarsaandeel" row
  // View mode: render the distribution key as a label-only line, since the
  // key is captured at cost-save time and cannot change after the fact.
  readOnly?: boolean;
  // Hide the key picker entirely — used when the caller drives the split via
  // consumption or manual amounts (no sleutel involved).
  hideKeyPicker?: boolean;
  // A billed cost: the VAT rate of our own a lease was billed at (see
  // costVat.utils), so its row shows the VAT its payment asked.
  outgoingVatPctByLease?: Record<string, number>;
  // Leases that price the cost's kind as a forfait: never billed, their
  // share stays with the owner (decision 20; fixedFeeBilling.utils).
  fixedFeeLeaseIds?: Set<string>;
  // A split by readings per occupant (occupantKey): each row shows its
  // own reading of the total and no days (M30).
  occupantReadings?: Record<string, number>;
}

export const BuildingDistribution: React.FC<BuildingDistributionProps> = ({
  keys,
  selectedKeyId,
  onSelectKey,
  breakdown,
  vatRate,
  billing,
  isShared,
  readOnly,
  hideKeyPicker,
  outgoingVatPctByLease = {},
  fixedFeeLeaseIds,
  occupantReadings,
}) => {
  const { periodDays } = breakdown;

  const grandTotal = roundCents(
    breakdown.units.reduce((s, u) => s + u.proration.total, 0),
  );

  const rowsByUnit = breakdown.units.map(u => {
    const ownerParty = { kind: "owner" as const, partyId: u.unitId };
    const rows: DisplayRow[] = [
      ...u.proration.shares.map(s => ({
        kind: "tenant" as const,
        // A forfait tenant's share is the owner's (decision 20).
        party: fixedFeeLeaseIds?.has(s.contractId)
          ? ownerParty
          : { kind: "tenant" as const, partyId: s.contractId },
        isFixedFee: !!fixedFeeLeaseIds?.has(s.contractId),
        key: s.contractId,
        name: s.tenantName,
        fromIso: s.fromIso,
        toIso: s.toIso,
        days: s.days,
        amount: s.amount,
      })),
      ...u.proration.vacancyIntervals.map((v, i) => ({
        kind: "vacancy" as const,
        party: ownerParty,
        key: `vac-${i}`,
        name: getLocalizedText("settlement.summary.vacancy"),
        fromIso: v.fromIso,
        toIso: v.toIso,
        days: v.days,
        amount: v.amount,
      })),
    ].sort((a, b) => (a.fromIso ?? "").localeCompare(b.fromIso ?? ""));

    // A negative owner share (a credit note) is a row too, or the rows would
    // not add up to the cost.
    if (isShared && Math.abs(u.proration.ownerBase) >= 0.005) {
      rows.push({
        kind: "owner",
        party: ownerParty,
        key: "ownerbase",
        name: getLocalizedText("settlement.summary.owner_share"),
        days: -1, // renders as "—"
        amount: u.proration.ownerBase,
      });
    }

    // A unit with no rows at all (0 weight) still shows one line.
    if (rows.length === 0) {
      rows.push({
        kind: "vacancy",
        party: ownerParty,
        key: "empty",
        name: getLocalizedText("settlement.summary.vacancy"),
        days: periodDays,
        amount: u.unitSlice,
      });
    }

    return rows;
  });

  // Each row's part of the cost's VAT: what the settlement or the tenant's
  // payment request bills it (see costVat.utils).
  const rowVat = costRowsVat({
    rows: rowsByUnit.flat().map(r => ({
      ...r.party,
      amount: r.amount,
      outgoingVatPct:
        r.party.kind === "tenant"
          ? outgoingVatPctByLease[r.party.partyId]
          : undefined,
    })),
    vatPct: vatRate,
    billing,
  });
  const vatTotal = vatOfAmount({ amountExclVat: grandTotal, vatPct: vatRate });
  const firstRowOfUnit = rowsByUnit.map((_, unitIndex) =>
    rowsByUnit.slice(0, unitIndex).reduce((n, rows) => n + rows.length, 0),
  );

  const tableRows = breakdown.units.flatMap((u, unitIndex) => {
    const rows = rowsByUnit[unitIndex];
    // Under readings: the row's own reading of the total, no days (M30).
    const contractIds = u.proration.shares.map(share => share.contractId);
    const readingOf = (r: DisplayRow, i: number): number | undefined => {
      if (!occupantReadings || r.kind === "owner") return undefined;
      // A unit's vacant days take one reading, on their first row.
      const isFirstVacancy =
        r.kind === "vacancy" &&
        rows.findIndex(row => row.kind === "vacancy") === i;
      if (r.kind === "vacancy" && !isFirstVacancy) return undefined;
      return readingRowShare({
        unitId: u.unitId,
        contractId: r.kind === "tenant" ? r.key : undefined,
        contractIds,
        values: occupantReadings,
        total: u.totalWeight,
      });
    };
    const isByReadings =
      !!occupantReadings &&
      readingRowShare({
        unitId: u.unitId,
        contractIds,
        values: occupantReadings,
        total: u.totalWeight,
      }) != null;
    const distributionOf = (r: DisplayRow, i: number): string => {
      if (!isByReadings) {
        return i === 0
          ? `${formatWeight(u.weight)} / ${formatWeight(u.totalWeight)}`
          : "";
      }
      const reading = readingOf(r, i);
      return reading == null
        ? "—"
        : `${formatWeight(reading)} / ${formatWeight(u.totalWeight)}`;
    };
    return rows.map((r, i) => ({
      id: `${u.unitId}-${r.key}`,
      content: [
        i === 0 ? <TextStyle variation="strong">{u.unitLabel}</TextStyle> : "",
        <div>
          {r.kind === "tenant" ? (
            r.name
          ) : (
            <TextStyle variation="subdued">{r.name}</TextStyle>
          )}
          {r.isFixedFee && (
            <div>
              <TextStyle variation="subdued" size="small">
                {getLocalizedText("settlement.distribution.owner_bears")}
              </TextStyle>
            </div>
          )}
          {r.fromIso && r.toIso && (
            <div>
              <TextStyle variation="subdued" size="small">
                {fmtNl(r.fromIso)} → {fmtNl(r.toIso)}
              </TextStyle>
            </div>
          )}
        </div>,
        distributionOf(r, i),
        isByReadings || r.days < 0 ? "—" : `${r.days} / ${periodDays}`,
        amountCell(r.amount, rowVat[firstRowOfUnit[unitIndex] + i]),
      ],
    }));
  });

  return (
    <Stack vertical spacing="tight">
      {hideKeyPicker ? null : readOnly ? (
        <div>
          <TextStyle variation="subdued">
            {getLocalizedText("settlement.distribution.title")}
          </TextStyle>
          <TextStyle variation="strong" element="div">
            {keys.find(k => k.id === selectedKeyId)?.name ?? "—"}
          </TextStyle>
        </div>
      ) : (
        <div style={{ maxWidth: 320 }}>
          <Select
            id="verdeelsleutel"
            label={getLocalizedText("settlement.distribution.title")}
            value={selectedKeyId}
            options={keys.map(k => ({
              value: k.id,
              label: keyDisplayName(k.name),
            }))}
            onChange={(opt: any) => opt?.value && onSelectKey(opt.value)}
          />
        </div>
      )}

      {breakdown.units.length > 0 ? (
        <AlignedTable>
          <DataTable
            columnContentTypes={["text", "text", "text", "text", "numeric"]}
            headings={[
              getLocalizedText("costs.field.property"),
              getLocalizedText("costs.field.rental_contract"),
              getLocalizedText("costs.field.distribution"),
              getLocalizedText("costs.field.temporal_basis"),
              getLocalizedText("costs.field.amount_excl_vat"),
            ]}
            rows={tableRows}
          />
          <TotalRow>
            <TextStyle variation="strong">
              {getLocalizedText("costs.total_of_cost")}
            </TextStyle>
            <div style={{ textAlign: "right" }}>
              {amountCell(grandTotal, vatTotal)}
            </div>
          </TotalRow>
        </AlignedTable>
      ) : (
        <TextStyle variation="subdued">
          {getLocalizedText("settlement.distribution.group_has_no_units")}
        </TextStyle>
      )}
    </Stack>
  );
};
